import { useState } from 'react';
import { DialpadIcon } from '@/icons';
import { StepFeedNumpad } from '@/components/jog/StepFeedNumpad';
import { Selector } from '@/components/jog/Selector';
import {
  useJogFeedRate,
  useJogIncrement,
  useSetJogFeedRate,
  useSetJogIncrement,
} from '@/store/store';
import styles from '@/components/jog/jog.module.css';

// Feed Rate in mm/min — value sent as the JOG speed.
const FEED_RATE_VALUES = [10, 50, 100, 200, 500, 750, 1000, 1500, 2000, 2500, 3000];

// Step Size in mm — one press moves the axis by exactly this far.
const STEP_VALUES = [0.005, 0.01, 0.05, 0.1, 0.2, 0.5, 1, 5, 10, 20, 50, 100];

/** Insert a value into a sorted number list (deduped), keeping ascending order. */
function insertSorted(list: number[], value: number): number[] {
  if (list.includes(value)) return list;
  return [...list, value].sort((a, b) => a - b);
}

/** Parse a numpad string to a finite number, or null if empty/invalid. */
function parseNumpad(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

/** Step Size + Feed Rate carousel pickers with numpad entry. Self-contained / reusable. */
export function JogSelectors(): JSX.Element {
  const feedRate = useJogFeedRate();
  const increment = useJogIncrement();
  const setFeedRate = useSetJogFeedRate();
  const setJogIncrement = useSetJogIncrement();

  // Option lists are stateful so custom numpad-entered values can be inserted + selected.
  const [stepOptions, setStepOptions] = useState<number[]>(STEP_VALUES);
  const [feedOptions, setFeedOptions] = useState<number[]>(FEED_RATE_VALUES);

  // Reflect the stored values back onto the carousels (fall back to the first option).
  const feedIndex = Math.max(0, feedOptions.indexOf(feedRate));
  const stepIndex = Math.max(0, stepOptions.indexOf(increment));

  // Controls which numpad bottom sheet is currently open (null = none)
  const [numpadTarget, setNumpadTarget] = useState<'step' | 'feed' | null>(null);

  const handleSubmit = (stepStr: string, feedStr: string): void => {
    const step = parseNumpad(stepStr);
    if (step !== null) {
      setStepOptions((list) => insertSorted(list, step));
      setJogIncrement(step);
    }
    const feed = parseNumpad(feedStr);
    if (feed !== null) {
      setFeedOptions((list) => insertSorted(list, feed));
      setFeedRate(feed);
    }
  };

  return (
    <div className={styles.jogSelectors}>
      <Selector
        label="Step Size (mm or deg)"
        options={stepOptions.map(String)}
        activeIndex={stepIndex}
        onChange={(i) => setJogIncrement(stepOptions[i] ?? 0.1)}
        windowSize={5}
        action={
          <button
            className={styles.selectorDialpadBtn}
            type="button"
            aria-label="Enter step size"
            onClick={() => setNumpadTarget('step')}
          >
            <DialpadIcon width={28} height={28} />
          </button>
        }
      />
      <Selector
        label="Feed Rate (mm/min or deg/min)"
        options={feedOptions.map(String)}
        activeIndex={feedIndex}
        onChange={(i) => setFeedRate(feedOptions[i] ?? 100)}
        windowSize={5}
        action={
          <button
            className={styles.selectorDialpadBtn}
            type="button"
            aria-label="Enter feed rate"
            onClick={() => setNumpadTarget('feed')}
          >
            <DialpadIcon width={28} height={28} />
          </button>
        }
      />

      {numpadTarget !== null && (
        <StepFeedNumpad
          stepValue={increment}
          feedValue={feedRate}
          initialField={numpadTarget}
          onSubmit={handleSubmit}
          onClose={() => setNumpadTarget(null)}
        />
      )}
    </div>
  );
}
